import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { FormDialog } from '@/components/ui/form-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pencil } from 'lucide-react';
import { useSearch } from '@/contexts/SearchContext';
import { useAuth } from '@/contexts/AuthContext';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { vendorPortalApi, type HospitalVendor } from '@/api/vendorPortal';
import { fomratString } from '@/utils/stringUtils';
import { normalizePersonName } from '@/lib/personName';
import { SPECIALISATION_OPTIONS, CUSTOM_SPECIALISATION_VALUE } from '@/constants/doctorSpecialization';
import ShiftsCalendar from '@/components/shifts/ShiftsCalender';
import { Staff, StaffFormData, StaffOnboardingFiles } from '@/types/types';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { needsDoctorProfileFields } from '@/lib/clinicalRoles';
import { formatDate } from '@/utils/dateUtils';
import InvestorReportAccessPicker from '@/components/investor/InvestorReportAccessPicker';
import type { InvestorBusinessReportKey } from '@/constants/investorBusinessReports';
import { doctorTeamApi } from '@/api/doctorTeam';
import {
  DoctorTeamBrandingFields,
  type DoctorTeamBrandingForm,
} from '@/components/staff/DoctorTeamBrandingFields';

const emptyTeamBranding = (): DoctorTeamBrandingForm => ({
  teamPortalPrimaryColor: '#6F553D',
  teamPortalSecondaryColor: '#707B82',
  teamPortalAccentColor: '#B07A3E',
  teamPortalSidebarBgColor: '#F9F6F1',
  teamPortalCustomUiColors: false,
});

/** Normalize API / DB role strings so UI checks match (e.g. SALES_PERSON). */
function normalizeStaffRole(role: string | undefined | null): string {
  if (!role) return '';
  return String(role).trim().toUpperCase().replace(/\s+/g, '_');
}

const emptyOnboardingFiles = (): StaffOnboardingFiles => ({
  panCard: null,
  cancelledCheque: null,
  registrationDocument: null,
  indemnityInsurance: null,
});

function ExistingDocLink({ url, label }: { url?: string | null; label: string }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline">
      View uploaded {label}
    </a>
  );
}

function StaffOnboardingFields({
  idPrefix,
  panNumber,
  onPanNumberChange,
  registrationNumber,
  onRegistrationNumberChange,
  files,
  onFileChange,
  existingUrls,
}: {
  idPrefix: string;
  panNumber: string;
  onPanNumberChange: (value: string) => void;
  registrationNumber: string;
  onRegistrationNumberChange: (value: string) => void;
  files: StaffOnboardingFiles;
  onFileChange: (field: keyof StaffOnboardingFiles, file: File | null) => void;
  existingUrls?: {
    panCardUrl?: string | null;
    cancelledChequeUrl?: string | null;
    registrationDocumentUrl?: string | null;
    indemnityInsuranceUrl?: string | null;
  };
}) {
  const fileHint = (file: File | null | undefined) =>
    file ? <p className="text-xs text-muted-foreground mt-1">Selected: {file.name}</p> : null;

  return (
    <div className="space-y-4 rounded-md border border-gray-200 bg-gray-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Onboarding documents</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Optional. Upload PDF or image files (max 10 MB each).
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-pan-number`}>PAN number</Label>
        <Input
          id={`${idPrefix}-pan-number`}
          value={panNumber}
          onChange={(e) => onPanNumberChange(e.target.value.toUpperCase())}
          placeholder="e.g. ABCDE1234F"
          maxLength={10}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-pan-card`}>PAN card</Label>
        <Input
          id={`${idPrefix}-pan-card`}
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => onFileChange('panCard', e.target.files?.[0] ?? null)}
        />
        {fileHint(files.panCard)}
        <ExistingDocLink url={existingUrls?.panCardUrl} label="PAN card" />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-cancelled-cheque`}>Cancelled cheque</Label>
        <Input
          id={`${idPrefix}-cancelled-cheque`}
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => onFileChange('cancelledCheque', e.target.files?.[0] ?? null)}
        />
        {fileHint(files.cancelledCheque)}
        <ExistingDocLink url={existingUrls?.cancelledChequeUrl} label="cancelled cheque" />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-registration-number`}>Registration number</Label>
        <Input
          id={`${idPrefix}-registration-number`}
          value={registrationNumber}
          onChange={(e) => onRegistrationNumberChange(e.target.value)}
          placeholder="Medical council / registration no."
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-registration-document`}>Registration document</Label>
        <Input
          id={`${idPrefix}-registration-document`}
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => onFileChange('registrationDocument', e.target.files?.[0] ?? null)}
        />
        {fileHint(files.registrationDocument)}
        <ExistingDocLink url={existingUrls?.registrationDocumentUrl} label="registration document" />
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-indemnity-insurance`}>Indemnity insurance</Label>
        <Input
          id={`${idPrefix}-indemnity-insurance`}
          type="file"
          accept=".pdf,image/*"
          onChange={(e) => onFileChange('indemnityInsurance', e.target.files?.[0] ?? null)}
        />
        {fileHint(files.indemnityInsurance)}
        <ExistingDocLink url={existingUrls?.indemnityInsuranceUrl} label="indemnity insurance" />
      </div>
    </div>
  );
}

function findStaffByName(staffs: Staff[] | undefined, name: string): Staff[] {
  const normalized = name.trim().toLowerCase();
  if (!normalized || !staffs?.length) return [];
  return staffs.filter((s) => s.name.trim().toLowerCase() === normalized);
}

type StaffHospitalScope = 'HOSPITAL' | 'MULTI_HOSPITAL' | 'CENTRAL';

function staffScopeLabel(scope?: StaffHospitalScope): string {
  switch (scope) {
    case 'MULTI_HOSPITAL':
      return 'Multiple hospitals';
    case 'CENTRAL':
      return 'Central';
    default:
      return 'Single hospital';
  }
}

type ChainHospitalOption = { id: string; name: string };

function StaffHospitalAssignmentFields({
  idPrefix,
  scope,
  onScopeChange,
  primaryHospitalId,
  onPrimaryHospitalIdChange,
  multiHospitalIds,
  onMultiHospitalIdsChange,
  chainHospitals,
  helperText,
}: {
  idPrefix: string;
  scope: StaffHospitalScope;
  onScopeChange: (scope: StaffHospitalScope) => void;
  primaryHospitalId: string;
  onPrimaryHospitalIdChange: (id: string) => void;
  multiHospitalIds: string[];
  onMultiHospitalIdsChange: (ids: string[]) => void;
  chainHospitals: ChainHospitalOption[];
  helperText?: ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50/40 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Hospital assignment</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Choose whether this user belongs to one site, selected sites, or the full chain.
          {helperText}
        </p>
      </div>
      <div className="space-y-2">
        <Label>Scope</Label>
        <Select
          value={scope}
          onValueChange={(v) => {
            const next = v as StaffHospitalScope;
            onScopeChange(next);
            if (next === 'HOSPITAL') {
              onMultiHospitalIdsChange([]);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="HOSPITAL">Single hospital</SelectItem>
            <SelectItem value="MULTI_HOSPITAL">Multiple hospitals</SelectItem>
            <SelectItem value="CENTRAL">Central (chain-wide)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {scope === 'HOSPITAL' && (
        <div className="space-y-2">
          <Label>Hospital</Label>
          <Select
            value={primaryHospitalId}
            onValueChange={onPrimaryHospitalIdChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select hospital" />
            </SelectTrigger>
            <SelectContent>
              {chainHospitals.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {scope === 'MULTI_HOSPITAL' && (
        <div className="space-y-2">
          <Label>Hospitals (select two or more)</Label>
          <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border bg-white p-3">
            {chainHospitals.map((h) => {
              const checked = multiHospitalIds.includes(h.id);
              return (
                <div key={h.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`${idPrefix}-hospital-${h.id}`}
                    checked={checked}
                    onCheckedChange={(c) => {
                      const next = c
                        ? [...multiHospitalIds, h.id]
                        : multiHospitalIds.filter((id) => id !== h.id);
                      onMultiHospitalIdsChange(next);
                      if (next.length > 0 && !next.includes(primaryHospitalId)) {
                        onPrimaryHospitalIdChange(next[0]);
                      }
                    }}
                  />
                  <Label htmlFor={`${idPrefix}-hospital-${h.id}`} className="text-sm font-normal cursor-pointer">
                    {h.name}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {(scope === 'MULTI_HOSPITAL' || scope === 'CENTRAL') && (
        <div className="space-y-2">
          <Label>Primary hospital (for department)</Label>
          <Select value={primaryHospitalId} onValueChange={onPrimaryHospitalIdChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select primary hospital" />
            </SelectTrigger>
            <SelectContent>
              {(scope === 'MULTI_HOSPITAL'
                ? chainHospitals.filter((h) => multiHospitalIds.includes(h.id))
                : chainHospitals
              ).map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {scope === 'CENTRAL' && (
        <p className="text-xs text-muted-foreground">
          Central users can work across all hospitals in the chain. Department is anchored to the primary hospital above.
        </p>
      )}
    </div>
  );
}

function validateStaffHospitalAssignment(
  scope: StaffHospitalScope,
  primaryHospitalId: string,
  multiHospitalIds: string[]
): string | null {
  if (scope === 'HOSPITAL' && !primaryHospitalId) {
    return 'Select a hospital for this user';
  }
  if (scope === 'MULTI_HOSPITAL' && multiHospitalIds.length < 2) {
    return 'Select at least two hospitals for multi-site access';
  }
  if ((scope === 'MULTI_HOSPITAL' || scope === 'CENTRAL') && !primaryHospitalId) {
    return 'Select a primary hospital for department assignment';
  }
  return null;
}

function findStaffByEmail(staffs: Staff[] | undefined, email: string): Staff | undefined {
  const normalized = email.trim().toLowerCase();
  if (!normalized || !staffs?.length) return undefined;
  return staffs.find((s) => s.email.trim().toLowerCase() === normalized);
}

export default function UserManagement() {
  const { isChainAdmin, user: authUser } = useAuth();
  const chainHospitals = authUser?.hospitals ?? [];

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const queryClient = useQueryClient();
  const [role, setRole] = useState('DOCTOR');
  const [specialisation, setSpecialisation] = useState('');
  const [customSpecialisation, setCustomSpecialisation] = useState('');
  const [qualification, setQualification] = useState('');
  const [description, setDescription] = useState('');
  const [editRole, setEditRole] = useState('DOCTOR');
  const [editSpecialisation, setEditSpecialisation] = useState('');
  const [editCustomSpecialisation, setEditCustomSpecialisation] = useState('');
  const [editQualification, setEditQualification] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const { searchQuery } = useSearch();
  const [departmentId, setDepartmentId] = useState('');
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [addAgencyPartner, setAddAgencyPartner] = useState(false);
  const [addAgencyExtraSources, setAddAgencyExtraSources] = useState('');
  const [addPartnerCampaignPrefixes, setAddPartnerCampaignPrefixes] = useState('');
  const [addIsSalaried, setAddIsSalaried] = useState(false);
  const [editIsLeadAgency, setEditIsLeadAgency] = useState(false);
  const [editAgencyExtraSources, setEditAgencyExtraSources] = useState('');
  const [editPartnerCampaignPrefixes, setEditPartnerCampaignPrefixes] = useState('');
  const [editIsSalaried, setEditIsSalaried] = useState(false);
  const [panNumber, setPanNumber] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [addOnboardingFiles, setAddOnboardingFiles] = useState<StaffOnboardingFiles>(emptyOnboardingFiles);
  const [editPanNumber, setEditPanNumber] = useState('');
  const [editRegistrationNumber, setEditRegistrationNumber] = useState('');
  const [editOnboardingFiles, setEditOnboardingFiles] = useState<StaffOnboardingFiles>(emptyOnboardingFiles);
  const [duplicateNameWarningOpen, setDuplicateNameWarningOpen] = useState(false);
  const [duplicateNameMatches, setDuplicateNameMatches] = useState<Staff[]>([]);
  const [pendingAddStaff, setPendingAddStaff] = useState<{
    data: StaffFormData;
    files?: StaffOnboardingFiles;
  } | null>(null);
  const [addInvestorReportKeys, setAddInvestorReportKeys] = useState<InvestorBusinessReportKey[]>([]);
  const [editInvestorReportKeys, setEditInvestorReportKeys] = useState<InvestorBusinessReportKey[]>([]);
  const [addHospitalVendorId, setAddHospitalVendorId] = useState('');
  const [addRequiresOpsApproval, setAddRequiresOpsApproval] = useState(true);
  const [addStaffScope, setAddStaffScope] = useState<StaffHospitalScope>('HOSPITAL');
  const [addHospitalId, setAddHospitalId] = useState('');
  const [addMultiHospitalIds, setAddMultiHospitalIds] = useState<string[]>([]);
  const [addPrimaryHospitalId, setAddPrimaryHospitalId] = useState('');
  const [editStaffScope, setEditStaffScope] = useState<StaffHospitalScope>('HOSPITAL');
  const [editMultiHospitalIds, setEditMultiHospitalIds] = useState<string[]>([]);
  const [editPrimaryHospitalId, setEditPrimaryHospitalId] = useState('');
  const [addDoctorTeamLeadId, setAddDoctorTeamLeadId] = useState('');
  const [addPnlAestheticTeamLead, setAddPnlAestheticTeamLead] = useState(false);
  const [editPnlAestheticTeamLead, setEditPnlAestheticTeamLead] = useState(false);
  const [editDoctorTeamLeadId, setEditDoctorTeamLeadId] = useState('');
  const [addTeamBranding, setAddTeamBranding] = useState<DoctorTeamBrandingForm>(emptyTeamBranding);
  const [editTeamBranding, setEditTeamBranding] = useState<DoctorTeamBrandingForm>(emptyTeamBranding);
  const [addTeamLogoFile, setAddTeamLogoFile] = useState<File | null>(null);
  const [editTeamLogoFile, setEditTeamLogoFile] = useState<File | null>(null);

  const chainHasHospitals = isChainAdmin && chainHospitals.length > 1;

  const deptHospitalId = chainHasHospitals
    ? addStaffScope === 'HOSPITAL'
      ? addHospitalId || chainHospitals[0]?.id
      : addPrimaryHospitalId || addHospitalId || addMultiHospitalIds[0] || chainHospitals[0]?.id
    : undefined;

  const editStaffHospitalId = chainHasHospitals
    ? editStaffScope === 'HOSPITAL'
      ? editPrimaryHospitalId || selectedStaff?.hospitalId || chainHospitals[0]?.id
      : editPrimaryHospitalId || editMultiHospitalIds[0] || selectedStaff?.hospitalId || chainHospitals[0]?.id
    : selectedStaff?.hospitalId ?? selectedStaff?.hospital?.id ?? deptHospitalId;

  const { data: staffs, isLoading } = useQuery<Staff[]>({
    queryKey: ['hospital-staff', chainHasHospitals],
    queryFn: () =>
      hospitalAdminApi.getStaff({
        includeInactive: true,
        ...(chainHasHospitals ? { allHospitals: true } : {}),
      }),
  });

  const { data: departments } = useQuery({
    queryKey: ['departments', deptHospitalId],
    queryFn: () =>
      deptHospitalId
        ? hospitalAdminApi.getDepartments({ hospitalId: deptHospitalId })
        : hospitalAdminApi.getDepartments(),
    enabled: !chainHasHospitals || !!deptHospitalId,
  });

  const { data: editDepartments } = useQuery({
    queryKey: ['departments', 'edit', editStaffHospitalId],
    queryFn: () => hospitalAdminApi.getDepartments({ hospitalId: editStaffHospitalId! }),
    enabled: isEditDialogOpen && !!editStaffHospitalId,
  });

  const teamLeadsHospitalId =
    deptHospitalId || authUser?.hospitalId || chainHospitals[0]?.id;

  const { data: doctorTeamLeads = [] } = useQuery({
    queryKey: ['doctor-team-leads', teamLeadsHospitalId],
    queryFn: async () => {
      const res = await doctorTeamApi.listTeamLeads(teamLeadsHospitalId);
      return res.data.data?.items ?? [];
    },
    enabled: !!teamLeadsHospitalId && (isAddDialogOpen || isEditDialogOpen),
  });

  const { data: hospitalVendorsForLink = [] } = useQuery({
    queryKey: ['hospital-vendors-for-user-link'],
    queryFn: async () => {
      const res = await vendorPortalApi.listVendors();
      return (res.data?.data ?? []) as HospitalVendor[];
    },
    enabled: isAddDialogOpen && normalizeStaffRole(role) === 'VENDOR',
  });

  const filteredStaffs = staffs
    ?.filter((staff) => {
      if (!searchQuery) return true;
      return staff.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        staff.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (staff.specialisation || '').toLowerCase().includes(searchQuery.toLowerCase());
    })
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const addStaffMutation = useMutation({
    mutationFn: ({ data, files }: { data: StaffFormData; files?: StaffOnboardingFiles }) =>
      hospitalAdminApi.createStaff(data, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-staff'] });
      setIsAddDialogOpen(false);
      toast.success('Staff member added successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to add staff member');
      console.error('Add staff error:', error);
    },
  });

  const updateStaffMutation = useMutation({
    mutationFn: ({
      id,
      data,
      files,
    }: {
      id: string;
      data: Partial<StaffFormData>;
      files?: StaffOnboardingFiles;
    }) => hospitalAdminApi.updateStaff(id, data, files),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['hospital-staff'] });
      setIsEditDialogOpen(false);
      toast.success('Staff member updated successfully');
      if (variables.data.staffHospitalScope) {
        toast.message('Hospital access changed — ask the user to log out and back in.');
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update staff member');
      console.error('Update staff error:', error);
    },
  });

  const toggleStaffStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      hospitalAdminApi.updateStaff(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-staff'] });
      toast.success('Staff status updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update staff status');
      console.error('Update staff status error:', error);
    },
  });

  const submitAddStaff = (payload: { data: StaffFormData; files?: StaffOnboardingFiles }) => {
    addStaffMutation.mutate(payload);
  };

  const handleAddSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const effectiveSpec = needsDoctorProfileFields(role) && specialisation === CUSTOM_SPECIALISATION_VALUE ? customSpecialisation.trim() : specialisation;
    if (needsDoctorProfileFields(role) && !effectiveSpec) {
      toast.error(specialisation === CUSTOM_SPECIALISATION_VALUE ? 'Please enter a custom specialisation' : 'Please select a specialisation');
      return;
    }
    if (chainHasHospitals) {
      const scopeError = validateStaffHospitalAssignment(
        addStaffScope,
        addStaffScope === 'HOSPITAL' ? addHospitalId : addPrimaryHospitalId,
        addMultiHospitalIds
      );
      if (scopeError) {
        toast.error(scopeError);
        return;
      }
      if (!deptHospitalId) {
        toast.error('Select a hospital for department assignment');
        return;
      }
    }

    if (!departmentId) {
      toast.error('Please select a department');
      return;
    }
    if (normalizeStaffRole(role) === 'INVESTOR' && addInvestorReportKeys.length === 0) {
      toast.error('Select at least one business report for investor access');
      return;
    }

    const data: StaffFormData = {
      name: normalizePersonName(formData.get('name') as string),
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      role: formData.get('role') as string,
      specialisation: needsDoctorProfileFields(role) ? effectiveSpec : '',
      qualification: needsDoctorProfileFields(role) ? qualification : undefined,
      description: needsDoctorProfileFields(role) ? description : undefined,
      deptId: departmentId,
      panNumber: panNumber.trim() || undefined,
      registrationNumber: registrationNumber.trim() || undefined,
      isSalaried: addIsSalaried,
    };
    if (normalizeStaffRole(role) === 'SALES_PERSON') {
      data.isLeadAgency = addAgencyPartner;
      if (addAgencyPartner) {
        data.leadAgencyExtraSources = addAgencyExtraSources
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    if (normalizeStaffRole(role) === 'EXTERNAL_PARTNER') {
      data.leadAgencyExtraSources = addPartnerCampaignPrefixes
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (normalizeStaffRole(role) === 'INVESTOR') {
      data.investorReportKeys = addInvestorReportKeys;
    }
    if (normalizeStaffRole(role) === 'VENDOR') {
      if (addHospitalVendorId) data.hospitalVendorId = addHospitalVendorId;
      data.requiresOpsApproval = addRequiresOpsApproval;
    }
    if (normalizeStaffRole(role) === 'DOCTOR' || normalizeStaffRole(role) === 'RMO') {
      if (addDoctorTeamLeadId) data.doctorTeamLeadId = addDoctorTeamLeadId;
    }
    if (normalizeStaffRole(role) === 'DOCTOR_TEAM_LEAD') {
      Object.assign(data, addTeamBranding);
      if (addPnlAestheticTeamLead) data.pnlAestheticTeamLead = true;
    }

    if (chainHasHospitals) {
      data.staffHospitalScope = addStaffScope;
      if (addStaffScope === 'HOSPITAL') {
        data.hospitalId = addHospitalId || chainHospitals[0]?.id;
      } else if (addStaffScope === 'MULTI_HOSPITAL') {
        data.staffHospitalIds = addMultiHospitalIds;
        data.hospitalId = addPrimaryHospitalId || addMultiHospitalIds[0];
      } else {
        data.hospitalId = addPrimaryHospitalId || chainHospitals[0]?.id;
      }
    }

    const emailMatch = findStaffByEmail(staffs, data.email);
    if (emailMatch) {
      toast.error(
        `Email already in use by ${emailMatch.name} (${fomratString(emailMatch.role)}). Use a different email.`
      );
      return;
    }

    const nameMatches = findStaffByName(staffs, data.name);
    if (nameMatches.length > 0) {
      setPendingAddStaff({ data, files: addOnboardingFiles });
      setDuplicateNameMatches(nameMatches);
      setDuplicateNameWarningOpen(true);
      return;
    }

    const files: StaffOnboardingFiles = {
      ...addOnboardingFiles,
      teamPortalLogo: addTeamLogoFile,
    };
    submitAddStaff({ data, files });
  };

  const confirmAddDespiteDuplicateName = () => {
    if (!pendingAddStaff) return;
    submitAddStaff(pendingAddStaff);
    setDuplicateNameWarningOpen(false);
    setPendingAddStaff(null);
    setDuplicateNameMatches([]);
  };

  const handleEditSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedStaff) return;

    const formData = new FormData(e.currentTarget);
    const effectiveEditSpec = needsDoctorProfileFields(editRole) && editSpecialisation === CUSTOM_SPECIALISATION_VALUE ? editCustomSpecialisation.trim() : editSpecialisation;
    if (needsDoctorProfileFields(editRole) && !effectiveEditSpec) {
      toast.error(editSpecialisation === CUSTOM_SPECIALISATION_VALUE ? 'Please enter a custom specialisation' : 'Please select a specialisation');
      return;
    }
    if (normalizeStaffRole(editRole) === 'INVESTOR' && editInvestorReportKeys.length === 0) {
      toast.error('Select at least one business report for investor access');
      return;
    }
    if (chainHasHospitals) {
      const scopeError = validateStaffHospitalAssignment(
        editStaffScope,
        editPrimaryHospitalId,
        editMultiHospitalIds
      );
      if (scopeError) {
        toast.error(scopeError);
        return;
      }
      if (!editStaffHospitalId) {
        toast.error('Select a hospital for department assignment');
        return;
      }
    }
    if (!editDepartmentId) {
      toast.error('Please select a department');
      return;
    }
    const data: Partial<StaffFormData> = {
      name: normalizePersonName(formData.get('name') as string),
      email: formData.get('email') as string,
      role: editRole,
      specialisation: needsDoctorProfileFields(editRole) ? effectiveEditSpec : '',
      qualification: needsDoctorProfileFields(editRole) ? editQualification : undefined,
      description: needsDoctorProfileFields(editRole) ? editDescription : undefined,
      deptId: editDepartmentId,
      panNumber: editPanNumber.trim(),
      registrationNumber: editRegistrationNumber.trim(),
      isSalaried: editIsSalaried,
    };
    // Only include password if it's not empty
    const password = formData.get('password') as string;
    if (password) {
      data.password = password;
    }
    if (normalizeStaffRole(editRole) === 'SALES_PERSON') {
      data.isLeadAgency = editIsLeadAgency;
      data.leadAgencyExtraSources = editIsLeadAgency
        ? editAgencyExtraSources
            .split(/[,;\n]+/)
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
    }
    if (normalizeStaffRole(editRole) === 'EXTERNAL_PARTNER') {
      data.leadAgencyExtraSources = editPartnerCampaignPrefixes
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (normalizeStaffRole(editRole) === 'INVESTOR') {
      data.investorReportKeys = editInvestorReportKeys;
    }
    if (normalizeStaffRole(editRole) === 'DOCTOR' || normalizeStaffRole(editRole) === 'RMO') {
      data.doctorTeamLeadId = editDoctorTeamLeadId || undefined;
    }
    if (normalizeStaffRole(editRole) === 'DOCTOR_TEAM_LEAD') {
      Object.assign(data, editTeamBranding);
      if (editPnlAestheticTeamLead) data.pnlAestheticTeamLead = true;
    }
    if (chainHasHospitals) {
      data.staffHospitalScope = editStaffScope;
      if (editStaffScope === 'HOSPITAL') {
        data.hospitalId = editPrimaryHospitalId || selectedStaff.hospitalId || chainHospitals[0]?.id;
        data.staffHospitalIds = [];
      } else if (editStaffScope === 'MULTI_HOSPITAL') {
        data.staffHospitalIds = editMultiHospitalIds;
        data.hospitalId = editPrimaryHospitalId || editMultiHospitalIds[0];
      } else {
        data.hospitalId = editPrimaryHospitalId || chainHospitals[0]?.id;
        data.staffHospitalIds = [];
      }
    } else {
      const staffHospitalId = selectedStaff.hospitalId ?? selectedStaff.hospital?.id;
      if (staffHospitalId) {
        data.hospitalId = staffHospitalId;
      }
    }

    updateStaffMutation.mutate({
      id: selectedStaff.id,
      data,
      files: { ...editOnboardingFiles, teamPortalLogo: editTeamLogoFile },
    });
  };

  const handleToggleStatus = async (id: string, currentStatus: 'ACTIVE' | 'INACTIVE') => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const action = newStatus === 'ACTIVE' ? 'enable' : 'disable';

    if (window.confirm(`Are you sure you want to ${action} this staff member?`)) {
      toggleStaffStatusMutation.mutate({ id, status: newStatus });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }


  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">User Management</h1>
        <Button
          className="text-white bg-blue-800 hover:bg-blue-700"
          onClick={() => {
            setRole('DOCTOR');
            setSpecialisation('');
            setCustomSpecialisation('');
            setQualification('');
            setDescription('');
            setAddAgencyPartner(false);
            setAddAgencyExtraSources('');
            setAddIsSalaried(false);
            setAddInvestorReportKeys([]);
            setAddHospitalVendorId('');
            setAddRequiresOpsApproval(true);
            setPanNumber('');
            setRegistrationNumber('');
            setAddOnboardingFiles(emptyOnboardingFiles());
            setAddStaffScope('HOSPITAL');
            setAddHospitalId(chainHospitals[0]?.id ?? '');
            setAddMultiHospitalIds([]);
            setAddPrimaryHospitalId(chainHospitals[0]?.id ?? '');
            setDepartmentId('');
            setIsAddDialogOpen(true);
          }}
        >
          Add New User
        </Button>
      </div>

      <div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              {isChainAdmin && chainHospitals.length > 1 && (
                <>
                  <TableHead>Assignment</TableHead>
                  <TableHead>Hospital</TableHead>
                </>
              )}
              <TableHead>Created</TableHead>
              <TableHead>Agency portal</TableHead>
              <TableHead>Employment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
              <TableHead>Shifts</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStaffs?.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{fomratString(user.role)}</TableCell>
                {isChainAdmin && chainHospitals.length > 1 && (
                  <>
                    <TableCell className="text-sm text-muted-foreground">
                      {staffScopeLabel(user.staffHospitalScope)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.staffHospitalScope === 'CENTRAL'
                        ? 'Central'
                        : user.staffHospitalScope === 'MULTI_HOSPITAL'
                          ? `${user.staffHospitalIds?.length ?? 0} hospitals`
                          : user.hospital?.name ?? '—'}
                    </TableCell>
                  </>
                )}
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {user.createdAt ? formatDate(user.createdAt) : '—'}
                </TableCell>
                <TableCell>
                  {user.role === 'SALES_PERSON' && user.isLeadAgency ? (
                    <span className="text-xs text-amber-800 bg-amber-50 px-2 py-0.5 rounded">Read-only</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {user.isSalaried ? (
                    <span className="text-xs text-slate-800 bg-slate-100 px-2 py-0.5 rounded">Salaried</span>
                  ) : (
                    <span className="text-xs text-gray-600 bg-gray-50 px-2 py-0.5 rounded">Not salaried</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${user.status === 'ACTIVE'
                      ? 'bg-green-50 text-green-600'
                      : 'bg-red-50 text-red-600'
                      }`}
                  >
                    {user.status}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedStaff(user);
                        setEditRole(normalizeStaffRole(user.role) || user.role);
                        setEditIsLeadAgency(!!user.isLeadAgency);
                        setEditIsSalaried(!!user.isSalaried);
                        setEditAgencyExtraSources(
                          (user as Staff & { leadAgencyExtraSources?: string[] }).leadAgencyExtraSources?.join(
                            ', '
                          ) ?? ''
                        );
                        setEditPartnerCampaignPrefixes(
                          user.role === 'EXTERNAL_PARTNER'
                            ? (user as Staff & { leadAgencyExtraSources?: string[] }).leadAgencyExtraSources?.join(
                                ', '
                              ) ?? ''
                            : ''
                        );
                        setEditInvestorReportKeys(
                          ((user as Staff & { investorReportKeys?: string[] }).investorReportKeys ??
                            []) as InvestorBusinessReportKey[]
                        );
                        const spec = user.specialisation || '';
                        const isCustomSpec = spec && !SPECIALISATION_OPTIONS.includes(spec);
                        setEditSpecialisation(isCustomSpec ? CUSTOM_SPECIALISATION_VALUE : spec);
                        setEditCustomSpecialisation(isCustomSpec ? spec : '');
                        setEditQualification(user.qualification || '');
                        setEditDescription(user.description || '');
                        setEditDepartmentId(user.deptId || '');
                        setEditPanNumber(user.panNumber || '');
                        setEditRegistrationNumber(user.registrationNumber || '');
                        setEditDoctorTeamLeadId(user.doctorTeamLeadId || '');
                        setEditTeamBranding({
                          teamPortalPrimaryColor: user.teamPortalPrimaryColor || '#6F553D',
                          teamPortalSecondaryColor: user.teamPortalSecondaryColor || '#707B82',
                          teamPortalAccentColor: user.teamPortalAccentColor || '#B07A3E',
                          teamPortalSidebarBgColor: user.teamPortalSidebarBgColor || '#F9F6F1',
                          teamPortalCustomUiColors: !!user.teamPortalCustomUiColors,
                          teamPortalLogoUrl: user.teamPortalLogoUrl || '',
                        });
                        setEditTeamLogoFile(null);
                        setEditOnboardingFiles(emptyOnboardingFiles());
                        setEditStaffScope((user.staffHospitalScope as StaffHospitalScope) || 'HOSPITAL');
                        setEditMultiHospitalIds(
                          (user as Staff & { staffHospitalIds?: string[] }).staffHospitalIds ?? []
                        );
                        setEditPrimaryHospitalId(user.hospitalId || user.hospital?.id || chainHospitals[0]?.id || '');
                        setIsEditDialogOpen(true);
                      }}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <Pencil className="w-4 h-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(user.id, user.status)}
                      className={`p-1 hover:bg-gray-100 rounded-full ${user.status === 'ACTIVE' ? 'text-red-500' : 'text-green-500'
                        }`}
                    >
                      {user.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </TableCell>
                <TableCell>
                  <ShiftsCalendar userId={user.id} userName={user.name} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add User Dialog */}
      <FormDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        title="Add New User"
        onSubmit={handleAddSubmit}
        isLoading={addStaffMutation.isPending}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" name="name" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" name="email" type="email" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" name="password" type="password" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              name="role"
              value={role}
              onValueChange={(v) => {
                setRole(v);
                if (normalizeStaffRole(v) === 'RMO') setAddIsSalaried(true);
              }}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50 max-h-[200px] overflow-y-auto">
                <SelectItem value="DOCTOR">Doctor</SelectItem>
                <SelectItem value="DOCTOR_TEAM_LEAD">Doctor Team Lead (partner dashboard)</SelectItem>
                <SelectItem value="RMO">RMO (Resident Medical Officer)</SelectItem>
                <SelectItem value="NURSE">Nurse</SelectItem>
                <SelectItem value="RECEPTIONIST">Receptionist</SelectItem>
                <SelectItem value="OPERATIONS_MANAGER">Operations Manager</SelectItem>
                <SelectItem value="SALES_PERSON">Sales Person</SelectItem>
                <SelectItem value="SALES_MANAGER">Sales Manager</SelectItem>
                <SelectItem value="EXTERNAL_PARTNER">External Partner (CRM view-only)</SelectItem>
                <SelectItem value="LAB_TECHNICIAN">Lab Technician</SelectItem>
                <SelectItem value="PHARMACIST">Pharmacist</SelectItem>
                <SelectItem value="CHARTERED_ACCOUNTANT">Chartered Accountant (external)</SelectItem>
                <SelectItem value="INVESTOR">Investor (Business Reports only)</SelectItem>
                <SelectItem value="BUSINESS_ANALYST">Business Analyst (IPD, CRM, Finance & Reports)</SelectItem>
                <SelectItem value="VENDOR">Vendor (invoice portal)</SelectItem>
              </SelectContent>
            </Select>
            <input type="hidden" name="role" value={role} />
          </div>
          {chainHasHospitals && (
            <StaffHospitalAssignmentFields
              idPrefix="add"
              scope={addStaffScope}
              onScopeChange={(scope) => {
                setAddStaffScope(scope);
                setDepartmentId('');
              }}
              primaryHospitalId={addStaffScope === 'HOSPITAL' ? addHospitalId : addPrimaryHospitalId}
              onPrimaryHospitalIdChange={(id) => {
                setAddHospitalId(id);
                setAddPrimaryHospitalId(id);
                setDepartmentId('');
              }}
              multiHospitalIds={addMultiHospitalIds}
              onMultiHospitalIdsChange={(ids) => {
                setAddMultiHospitalIds(ids);
                setDepartmentId('');
              }}
              chainHospitals={chainHospitals}
              helperText={
                normalizeStaffRole(role) === 'CHARTERED_ACCOUNTANT' ? (
                  <>
                    {' '}
                    Use <strong>Multiple hospitals</strong> to let the CA switch between assigned sites in
                    the finance portal, or <strong>Central</strong> for every hospital in the chain.
                  </>
                ) : undefined
              }
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="department">Department</Label>
            <Select name="department" value={departmentId} onValueChange={setDepartmentId}>
              <SelectTrigger id="department">
                <SelectValue placeholder="Select a department" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50 max-h-[200px] overflow-y-auto">
                {departments?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chainHasHospitals && (
              <p className="text-xs text-muted-foreground">
                Re-select department after changing hospital assignment.
              </p>
            )}
          </div>
          {(normalizeStaffRole(role) === 'DOCTOR' || normalizeStaffRole(role) === 'RMO') && (
            <div className="space-y-2">
              <Label>Doctor team lead (optional)</Label>
              <Select value={addDoctorTeamLeadId || '__none__'} onValueChange={(v) => setAddDoctorTeamLeadId(v === '__none__' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Not part of a visiting doctor team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {doctorTeamLeads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {normalizeStaffRole(role) === 'DOCTOR_TEAM_LEAD' && (
            <>
              <DoctorTeamBrandingFields
                value={addTeamBranding}
                onChange={setAddTeamBranding}
                logoFile={addTeamLogoFile}
                onLogoFileChange={setAddTeamLogoFile}
              />
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="add-pnl-aesthetic-team"
                  checked={addPnlAestheticTeamLead}
                  onCheckedChange={(v) => setAddPnlAestheticTeamLead(v === true)}
                />
                <Label htmlFor="add-pnl-aesthetic-team" className="font-normal leading-snug">
                  Use this doctor team for P&amp;L <strong>No of Aesthetic Procedures</strong> (Additional
                  Services bills ≥ ₹5,000 for team doctors)
                </Label>
              </div>
            </>
          )}
          {normalizeStaffRole(role) === 'INVESTOR' && (
            <InvestorReportAccessPicker
              idPrefix="add"
              selected={addInvestorReportKeys}
              onChange={setAddInvestorReportKeys}
            />
          )}
          {normalizeStaffRole(role) === 'VENDOR' && (
            <div className="space-y-3 rounded-md border p-3 bg-muted/30">
              <p className="text-sm text-muted-foreground">
                Creates or links a vendor profile. Prefer creating the vendor under{' '}
                <strong>Vendor payments</strong> first, then pick it here.
              </p>
              <div className="space-y-2">
                <Label>Existing vendor profile (optional)</Label>
                <Select
                  value={addHospitalVendorId || '__new__'}
                  onValueChange={(v) => setAddHospitalVendorId(v === '__new__' ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Create new / link existing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__new__">Create new vendor profile from this user</SelectItem>
                    {hospitalVendorsForLink.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                        {v.staffId ? ' (already linked)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="add-vendor-ops"
                  checked={addRequiresOpsApproval}
                  onCheckedChange={(c) => setAddRequiresOpsApproval(c === true)}
                />
                <Label htmlFor="add-vendor-ops">
                  Require Operations Manager approval before admin payment
                </Label>
              </div>
            </div>
          )}
          {normalizeStaffRole(role) !== 'INVESTOR' && normalizeStaffRole(role) !== 'VENDOR' && (
          <div className="space-y-2">
            <Label htmlFor="add-is-salaried">Employment</Label>
            <Select
              value={addIsSalaried ? 'salaried' : 'not_salaried'}
              onValueChange={(v) => setAddIsSalaried(v === 'salaried')}
            >
              <SelectTrigger id="add-is-salaried">
                <SelectValue placeholder="Salaried or not" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                <SelectItem value="salaried">Salaried</SelectItem>
                <SelectItem value="not_salaried">Not salaried</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Used for P&amp;L doctor share. Salaried OPD is excluded from the 80% visiting share.
            </p>
          </div>
          )}
          {needsDoctorProfileFields(role) && (
            <>
              <div className="space-y-2">
                <Label htmlFor="specialisation">Specialisation</Label>
                <Select
                  name="specialisation"
                  value={specialisation}
                  onValueChange={(v) => {
                    setSpecialisation(v);
                    if (v !== CUSTOM_SPECIALISATION_VALUE) setCustomSpecialisation('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a specialisation" />
                  </SelectTrigger>
                  <SelectContent className="bg-white shadow-lg z-50 max-h-[280px] overflow-y-auto">
                    {SPECIALISATION_OPTIONS.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_SPECIALISATION_VALUE}>Custom specialisation</SelectItem>
                  </SelectContent>
                </Select>
                {specialisation === CUSTOM_SPECIALISATION_VALUE && (
                  <div className="pt-2">
                    <Input
                      id="custom-specialisation"
                      value={customSpecialisation}
                      onChange={(e) => setCustomSpecialisation(e.target.value)}
                      placeholder="Enter custom specialisation"
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="qualification">Qualification</Label>
                <Input
                  id="qualification"
                  name="qualification"
                  value={qualification}
                  onChange={(e) => setQualification(e.target.value)}
                  placeholder="e.g. MBBS, MD, Fellowship..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief bio or professional notes..."
                  rows={3}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </>
          )}
          {normalizeStaffRole(role) !== 'INVESTOR' && (
          <StaffOnboardingFields
            idPrefix="add"
            panNumber={panNumber}
            onPanNumberChange={setPanNumber}
            registrationNumber={registrationNumber}
            onRegistrationNumberChange={setRegistrationNumber}
            files={addOnboardingFiles}
            onFileChange={(field, file) =>
              setAddOnboardingFiles((prev) => ({ ...prev, [field]: file }))
            }
          />
          )}
          {normalizeStaffRole(role) === 'EXTERNAL_PARTNER' && (
            <div className="space-y-1 rounded-md border border-sky-200 bg-sky-50/80 p-3 ring-1 ring-sky-100">
              <Label htmlFor="add-partner-campaign-prefixes" className="text-sm font-medium">
                Campaign prefixes (optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                Restrict IPD Cases to Meta/Google campaigns whose name starts with these prefixes (e.g.{' '}
                <strong>GOQ</strong> for Go Quest). Leave blank to show all Meta/Google campaigns.
              </p>
              <textarea
                id="add-partner-campaign-prefixes"
                value={addPartnerCampaignPrefixes}
                onChange={(e) => setAddPartnerCampaignPrefixes(e.target.value)}
                placeholder="GOQ"
                rows={2}
                className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
              />
            </div>
          )}
          {normalizeStaffRole(role) === 'SALES_PERSON' && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/80 p-3 ring-1 ring-amber-100">
              <Checkbox
                id="add-agency-partner"
                checked={addAgencyPartner}
                onCheckedChange={(v) => setAddAgencyPartner(!!v)}
                className="mt-0.5 shrink-0"
              />
              <div className="space-y-1 min-w-0">
                <Label htmlFor="add-agency-partner" className="text-sm font-medium cursor-pointer">
                  External agency partner (read-only CRM)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Yes — create a dedicated partner login here: choose role <strong>Sales Person</strong> and tick this
                  box. Partner can sign in and see leads assigned to them but cannot edit status, bulk upload, or add
                  leads. Leave unchecked for your own sales team.
                </p>
                {addAgencyPartner && (
                  <div className="pt-2 space-y-1">
                    <Label htmlFor="add-agency-extra-sources" className="text-xs font-medium">
                      Extra lead sources (optional)
                    </Label>
                    <textarea
                      id="add-agency-extra-sources"
                      value={addAgencyExtraSources}
                      onChange={(e) => setAddAgencyExtraSources(e.target.value)}
                      placeholder="e.g. EXOTEL, GOOGLE_PAID, META — comma or newline separated. Partner also sees all hospital leads with these sources."
                      rows={2}
                      className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </FormDialog>

      <Dialog
        open={duplicateNameWarningOpen}
        onOpenChange={(open) => {
          setDuplicateNameWarningOpen(open);
          if (!open) {
            setPendingAddStaff(null);
            setDuplicateNameMatches([]);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Name already exists</DialogTitle>
            <DialogDescription>
              A user with the name{' '}
              <strong>{pendingAddStaff?.data.name.trim()}</strong> already exists in this hospital.
              You can still create another account with the same display name if intended.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-40 space-y-2 overflow-y-auto rounded-md border bg-muted/30 p-3 text-sm">
            {duplicateNameMatches.map((match) => (
              <li key={match.id} className="flex flex-col gap-0.5">
                <span className="font-medium">{match.name}</span>
                <span className="text-muted-foreground text-xs">
                  {match.email} · {fomratString(match.role)}
                  {match.status === 'INACTIVE' ? ' · Inactive' : ''}
                </span>
              </li>
            ))}
          </ul>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDuplicateNameWarningOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmAddDespiteDuplicateName}
              disabled={addStaffMutation.isPending}
            >
              Create anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <FormDialog
        key={selectedStaff?.id ? `edit-${selectedStaff.id}` : 'edit-closed'}
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedStaff(null);
          setEditCustomSpecialisation('');
        }}
        title="Edit User"
        onSubmit={handleEditSubmit}
        isLoading={updateStaffMutation.isPending}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              name="name"
              defaultValue={selectedStaff?.name}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              name="email"
              type="email"
              defaultValue={selectedStaff?.email}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
            <Input id="edit-password" name="password" type="password" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-role">Role</Label>
            <Select name="role" value={editRole} onValueChange={setEditRole} disabled>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                <SelectItem value="DOCTOR">Doctor</SelectItem>
                <SelectItem value="DOCTOR_TEAM_LEAD">Doctor Team Lead (partner dashboard)</SelectItem>
                <SelectItem value="RMO">RMO (Resident Medical Officer)</SelectItem>
                <SelectItem value="NURSE">Nurse</SelectItem>
                <SelectItem value="RECEPTIONIST">Receptionist</SelectItem>
                <SelectItem value="OPERATIONS_MANAGER">Operations Manager</SelectItem>
                <SelectItem value="SALES_PERSON">Sales Person</SelectItem>
                <SelectItem value="SALES_MANAGER">Sales Manager</SelectItem>
                <SelectItem value="EXTERNAL_PARTNER">External Partner (CRM view-only)</SelectItem>
                <SelectItem value="LAB_TECHNICIAN">Lab Technician</SelectItem>
                <SelectItem value="PHARMACIST">Pharmacist</SelectItem>
                <SelectItem value="CHARTERED_ACCOUNTANT">Chartered Accountant (external)</SelectItem>
                <SelectItem value="INVESTOR">Investor (Business Reports only)</SelectItem>
                <SelectItem value="BUSINESS_ANALYST">Business Analyst (IPD, CRM, Finance & Reports)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {chainHasHospitals && (
            <StaffHospitalAssignmentFields
              idPrefix="edit"
              scope={editStaffScope}
              onScopeChange={(scope) => {
                setEditStaffScope(scope);
                setEditDepartmentId('');
              }}
              primaryHospitalId={editPrimaryHospitalId}
              onPrimaryHospitalIdChange={(id) => {
                setEditPrimaryHospitalId(id);
                setEditDepartmentId('');
              }}
              multiHospitalIds={editMultiHospitalIds}
              onMultiHospitalIdsChange={(ids) => {
                setEditMultiHospitalIds(ids);
                setEditDepartmentId('');
              }}
              chainHospitals={chainHospitals}
              helperText={
                normalizeStaffRole(editRole) === 'SALES_MANAGER' ? (
                  <>
                    {' '}
                    Sales managers always see CRM leads from all hospitals in the chain; use{' '}
                    <strong>Central</strong> for chain-wide booking and queue access too.
                  </>
                ) : normalizeStaffRole(editRole) === 'CHARTERED_ACCOUNTANT' ? (
                  <>
                    {' '}
                    Use <strong>Multiple hospitals</strong> for selected sites, or{' '}
                    <strong>Central</strong> for chain-wide finance access.
                  </>
                ) : undefined
              }
            />
          )}
          <div className="space-y-2">
            <Label htmlFor="edit-department">Department</Label>
            <Select name="department" value={editDepartmentId} onValueChange={setEditDepartmentId}>
              <SelectTrigger id="edit-department">
                <SelectValue placeholder="Select a department" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                {(editDepartments ?? departments)?.map((dept) => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chainHasHospitals && (
              <p className="text-xs text-muted-foreground">
                Re-select department after changing hospital assignment.
              </p>
            )}
          </div>
          {(normalizeStaffRole(editRole) === 'DOCTOR' || normalizeStaffRole(editRole) === 'RMO') && (
            <div className="space-y-2">
              <Label>Doctor team lead (optional)</Label>
              <Select
                value={editDoctorTeamLeadId || '__none__'}
                onValueChange={(v) => setEditDoctorTeamLeadId(v === '__none__' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Not part of a visiting doctor team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {doctorTeamLeads.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {normalizeStaffRole(editRole) === 'DOCTOR_TEAM_LEAD' && (
            <>
              <DoctorTeamBrandingFields
                value={editTeamBranding}
                onChange={setEditTeamBranding}
                logoFile={editTeamLogoFile}
                onLogoFileChange={setEditTeamLogoFile}
                existingLogoUrl={selectedStaff?.teamPortalLogoUrl}
              />
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="edit-pnl-aesthetic-team"
                  checked={editPnlAestheticTeamLead}
                  onCheckedChange={(v) => setEditPnlAestheticTeamLead(v === true)}
                />
                <Label htmlFor="edit-pnl-aesthetic-team" className="font-normal leading-snug">
                  Use this doctor team for P&amp;L <strong>No of Aesthetic Procedures</strong> (Additional
                  Services bills ≥ ₹5,000 for team doctors)
                </Label>
              </div>
            </>
          )}
          {normalizeStaffRole(editRole) === 'INVESTOR' && (
            <InvestorReportAccessPicker
              idPrefix="edit"
              selected={editInvestorReportKeys}
              onChange={setEditInvestorReportKeys}
            />
          )}
          {normalizeStaffRole(editRole) !== 'INVESTOR' && (
          <div className="space-y-2">
            <Label htmlFor="edit-is-salaried">Employment</Label>
            <Select
              value={editIsSalaried ? 'salaried' : 'not_salaried'}
              onValueChange={(v) => setEditIsSalaried(v === 'salaried')}
            >
              <SelectTrigger id="edit-is-salaried">
                <SelectValue placeholder="Salaried or not" />
              </SelectTrigger>
              <SelectContent className="bg-white shadow-lg z-50">
                <SelectItem value="salaried">Salaried</SelectItem>
                <SelectItem value="not_salaried">Not salaried</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}
          {(normalizeStaffRole(editRole) === 'EXTERNAL_PARTNER' ||
            normalizeStaffRole(selectedStaff?.role) === 'EXTERNAL_PARTNER') && (
            <div className="space-y-1 rounded-md border border-sky-200 bg-sky-50/80 p-3 ring-1 ring-sky-100">
              <Label htmlFor="edit-partner-campaign-prefixes" className="text-sm font-medium">
                Campaign prefixes (optional)
              </Label>
              <p className="text-xs text-muted-foreground">
                e.g. <strong>GOQ</strong> — partner sees only matching Meta/Google campaigns on IPD Cases.
              </p>
              <textarea
                id="edit-partner-campaign-prefixes"
                value={editPartnerCampaignPrefixes}
                onChange={(e) => setEditPartnerCampaignPrefixes(e.target.value)}
                placeholder="GOQ"
                rows={2}
                className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
              />
            </div>
          )}
          {(normalizeStaffRole(editRole) === 'SALES_PERSON' ||
            normalizeStaffRole(selectedStaff?.role) === 'SALES_PERSON') && (
            <div className="flex items-start gap-2 rounded-md border-2 border-amber-300 bg-amber-50 p-3 shadow-sm">
              <Checkbox
                id="edit-agency-partner"
                checked={editIsLeadAgency}
                onCheckedChange={(v) => setEditIsLeadAgency(!!v)}
                className="mt-0.5 shrink-0 h-5 w-5"
              />
              <div className="space-y-1 min-w-0">
                <Label htmlFor="edit-agency-partner" className="text-sm font-semibold cursor-pointer">
                  External agency partner (read-only CRM)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Checked: partner login — view-only on leads (no edits, no bulk upload). Unchecked: full sales access.
                  Save after changing.
                </p>
                {editIsLeadAgency && (
                  <div className="pt-2 space-y-1">
                    <Label htmlFor="edit-agency-extra-sources" className="text-xs font-medium">
                      Extra lead sources (optional)
                    </Label>
                    <textarea
                      id="edit-agency-extra-sources"
                      value={editAgencyExtraSources}
                      onChange={(e) => setEditAgencyExtraSources(e.target.value)}
                      placeholder="e.g. EXOTEL, GOOGLE_PAID — comma separated"
                      rows={2}
                      className="flex min-h-[56px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          {needsDoctorProfileFields(editRole) && (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-specialisation">Specialisation</Label>
                <Select
                  name="specialisation"
                  value={editSpecialisation}
                  onValueChange={(v) => {
                    setEditSpecialisation(v);
                    if (v !== CUSTOM_SPECIALISATION_VALUE) setEditCustomSpecialisation('');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a specialisation" />
                  </SelectTrigger>
                  <SelectContent className="bg-white shadow-lg z-50 max-h-[280px] overflow-y-auto">
                    {SPECIALISATION_OPTIONS.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_SPECIALISATION_VALUE}>Custom specialisation</SelectItem>
                  </SelectContent>
                </Select>
                {editSpecialisation === CUSTOM_SPECIALISATION_VALUE && (
                  <div className="pt-2">
                    <Input
                      id="edit-custom-specialisation"
                      value={editCustomSpecialisation}
                      onChange={(e) => setEditCustomSpecialisation(e.target.value)}
                      placeholder="Enter custom specialisation"
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-qualification">Qualification</Label>
                <Input
                  id="edit-qualification"
                  name="qualification"
                  value={editQualification}
                  onChange={(e) => setEditQualification(e.target.value)}
                  placeholder="e.g. MBBS, MD, Fellowship..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <textarea
                  id="edit-description"
                  name="description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Brief bio or professional notes..."
                  rows={3}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </>
          )}
          <StaffOnboardingFields
            idPrefix="edit"
            panNumber={editPanNumber}
            onPanNumberChange={setEditPanNumber}
            registrationNumber={editRegistrationNumber}
            onRegistrationNumberChange={setEditRegistrationNumber}
            files={editOnboardingFiles}
            onFileChange={(field, file) =>
              setEditOnboardingFiles((prev) => ({ ...prev, [field]: file }))
            }
            existingUrls={{
              panCardUrl: selectedStaff?.panCardUrl,
              cancelledChequeUrl: selectedStaff?.cancelledChequeUrl,
              registrationDocumentUrl: selectedStaff?.registrationDocumentUrl,
              indemnityInsuranceUrl: selectedStaff?.indemnityInsuranceUrl,
            }}
          />
        </div>
      </FormDialog>
    </div>
  );
} 